import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
    AlertCircle,
    Trash2,
    XCircle,
    Minus,
    Edit3,
    Save,
    DollarSign,
    TrendingUp,
    TrendingDown,
    Clock,
    Target,
    Shield,
} from 'lucide-react';
import { useUpdateOrder, useCancelOrder, useCloseOrder } from '@/api/orders/use-orders';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { formatAmount } from '@/utils/formatters';

interface Order {
    id: string;
    instrument?: {
        symbol: string;
    };
    side: 'buy' | 'sell';
    type: string;
    lot_size: number;
    price?: number;
    stop_price?: number;
    stop_loss_price?: number;
    take_profit_price?: number;
    status: string;
    created_at: string;
}

interface OrderEditDialogProps {
    isOpen: boolean;
    onClose: () => void;
    order: Order | null;
    onOrderUpdated?: () => void;
}

interface OrderFormData {
    price: string;
    stopLoss: string;
    takeProfit: string;
    partialCloseAmount: string;
}

// Helper function to get the initial price based on order type
const getInitialPrice = (order: Order | null): string => {
    if (!order) return '';

    // For stop orders, use stop_price; for limit orders, use price
    if (order.type === 'stop' || order.type === 'stop_limit') {
        return order.stop_price?.toString() || '';
    }

    return order.price?.toString() || '';
};

export const OrderEditDialog = ({
    isOpen,
    onClose,
    order,
    onOrderUpdated,
}: OrderEditDialogProps) => {
    const { enqueueSnackbar } = useSnackbar();
    const updateOrderMutation = useUpdateOrder();
    const cancelOrderMutation = useCancelOrder();
    const closeOrderMutation = useCloseOrder();

    // State for alert dialogs
    const [partialCloseDialogOpen, setPartialCloseDialogOpen] = React.useState(false);
    const [cancelDialogOpen, setCancelDialogOpen] = React.useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);

    const {
        register,
        handleSubmit,
        reset,
        getValues,
        formState: { errors },
    } = useForm<OrderFormData>({
        defaultValues: {
            price: getInitialPrice(order),
            stopLoss: order?.stop_loss_price?.toString() || '',
            takeProfit: order?.take_profit_price?.toString() || '',
            partialCloseAmount: '',
        },
    });

    // Reset form when order changes
    React.useEffect(() => {
        if (order) {
            reset({
                price: getInitialPrice(order),
                stopLoss: order.stop_loss_price?.toString() || '',
                takeProfit: order.take_profit_price?.toString() || '',
                partialCloseAmount: '',
            });
        }
    }, [order, reset]);

    const onSubmit = async (data: OrderFormData) => {
        if (!order) return;

        try {
            const updateData: any = {};

            // Handle price based on order type
            if (data.price) {
                if (order.type === 'stop' || order.type === 'stop_limit') {
                    updateData.stop_price = parseFloat(data.price);
                } else {
                    updateData.price = parseFloat(data.price);
                }
            }
            if (data.stopLoss) {
                updateData.stop_loss_price = parseFloat(data.stopLoss);
            }
            if (data.takeProfit) {
                updateData.take_profit_price = parseFloat(data.takeProfit);
            }

            await updateOrderMutation.mutateAsync({
                id: order.id,
                input: updateData,
            });

            enqueueSnackbar('Order updated successfully', { variant: 'success' });
            onOrderUpdated?.();
            onClose();
        } catch (error: any) {
            const message = error.response?.data?.message || 'Failed to update order';
            enqueueSnackbar(message, { variant: 'error' });
        }
    };

    const handlePartialClose = async (data: OrderFormData) => {
        if (!order || !data.partialCloseAmount) return;

        const closeAmount = parseFloat(data.partialCloseAmount);
        if (closeAmount <= 0 || closeAmount >= order.lot_size) {
            enqueueSnackbar('Partial close amount must be between 0 and current lot size', {
                variant: 'error',
            });
            return;
        }

        setPartialCloseDialogOpen(true);
    };

    const confirmPartialClose = async () => {
        if (!order) return;

        const formData = getValues();
        const closeAmount = parseFloat(formData.partialCloseAmount);

        try {
            await closeOrderMutation.mutateAsync({
                id: order.id,
                close_type: 'partial',
                close_amount: closeAmount,
            });
            enqueueSnackbar('Order partially closed successfully', { variant: 'success' });
            onOrderUpdated?.();
            setPartialCloseDialogOpen(false);
            onClose();
        } catch (error: any) {
            const message = error.response?.data?.message || 'Failed to partially close order';
            enqueueSnackbar(message, { variant: 'error' });
        }
    };

    const handleCancelOrder = async () => {
        if (!order) return;
        setCancelDialogOpen(true);
    };

    const confirmCancelOrder = async () => {
        if (!order) return;

        try {
            await cancelOrderMutation.mutateAsync(order.id);
            enqueueSnackbar('Order cancelled successfully', { variant: 'success' });
            onOrderUpdated?.();
            setCancelDialogOpen(false);
            onClose();
        } catch (error: any) {
            const message = error.response?.data?.message || 'Failed to cancel order';
            enqueueSnackbar(message, { variant: 'error' });
        }
    };

    const handleDeleteOrder = async () => {
        if (!order) return;
        setDeleteDialogOpen(true);
    };

    const confirmDeleteOrder = async () => {
        if (!order) return;

        try {
            await cancelOrderMutation.mutateAsync(order.id);
            enqueueSnackbar('Order deleted successfully', { variant: 'success' });
            onOrderUpdated?.();
            setDeleteDialogOpen(false);
            onClose();
        } catch (error: any) {
            const message = error.response?.data?.message || 'Failed to delete order';
            enqueueSnackbar(message, { variant: 'error' });
        }
    };

    if (!order) return null;

    return (
        <>
            <Dialog open={isOpen} onOpenChange={onClose}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-semibold flex items-center gap-2">
                            <Edit3 className="h-5 w-5" />
                            Edit Order - {order.instrument?.symbol || 'N/A'}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-6">
                        {/* Order Overview */}
                        <Card>
                            <CardHeader className="pb-3">
                                <CardTitle className="text-lg flex items-center gap-2">
                                    <Target className="h-4 w-4" />
                                    Order Overview
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-muted-foreground">
                                                Side:
                                            </span>
                                            <Badge
                                                variant={
                                                    order.side === 'buy' ? 'default' : 'destructive'
                                                }
                                                className="capitalize"
                                            >
                                                {order.side === 'buy' ? (
                                                    <>
                                                        <TrendingUp className="h-3 w-3 mr-1" /> Buy
                                                    </>
                                                ) : (
                                                    <>
                                                        <TrendingDown className="h-3 w-3 mr-1" />{' '}
                                                        Sell
                                                    </>
                                                )}
                                            </Badge>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-muted-foreground">
                                                Type:
                                            </span>
                                            <Badge variant="outline" className="capitalize">
                                                {order.type}
                                            </Badge>
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-muted-foreground">
                                                Lot Size:
                                            </span>
                                            <span className="text-sm font-medium">
                                                {formatAmount(Number(order.lot_size || 0), 5)}
                                            </span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-muted-foreground">
                                                Status:
                                            </span>
                                            <Badge variant="secondary" className="capitalize">
                                                {order.status}
                                            </Badge>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-muted-foreground">Created:</span>
                                    <span className="text-sm font-medium">
                                        {new Date(order.created_at).toLocaleString()}
                                    </span>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Edit Form */}
                        <Card>
                            <CardHeader className="pb-3">
                                <CardTitle className="text-lg flex items-center gap-2">
                                    <Edit3 className="h-4 w-4" />
                                    Order Parameters
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                                    {/* Price Section */}
                                    <div className="space-y-4">
                                        <div className="flex items-center gap-2">
                                            <DollarSign className="h-4 w-4 text-muted-foreground" />
                                            <Label className="text-base font-medium">
                                                Price Settings
                                            </Label>
                                        </div>

                                        <div>
                                            <Label className="text-sm text-muted-foreground mb-2 block font-medium">
                                                {order.type === 'limit'
                                                    ? 'Limit Price'
                                                    : order.type === 'stop'
                                                      ? 'Stop Price'
                                                      : order.type === 'stop_limit'
                                                        ? 'Stop Limit Price'
                                                        : 'Price'}
                                            </Label>
                                            <Input
                                                type="number"
                                                step="0.00001"
                                                placeholder="0.00000"
                                                {...register('price', {
                                                    min: {
                                                        value: 0,
                                                        message: 'Price must be positive',
                                                    },
                                                    required: 'Price is required',
                                                })}
                                                className="h-10 text-sm"
                                            />
                                            {order.type === 'limit' && (
                                                <p className="text-xs text-muted-foreground mt-1">
                                                    {order.side === 'buy'
                                                        ? 'Order will execute when market price reaches or goes below this price'
                                                        : 'Order will execute when market price reaches or goes above this price'}
                                                </p>
                                            )}
                                            {order.type === 'stop' && (
                                                <p className="text-xs text-muted-foreground mt-1">
                                                    Order will be triggered when market price
                                                    reaches this level
                                                </p>
                                            )}
                                            {errors.price && (
                                                <div className="flex items-center space-x-1 mt-1 text-sm text-red-500">
                                                    <AlertCircle className="h-4 w-4" />
                                                    <span>{errors.price.message}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <Separator />

                                    {/* Risk Management Section */}
                                    <div className="space-y-4">
                                        <div className="flex items-center gap-2">
                                            <Shield className="h-4 w-4 text-muted-foreground" />
                                            <Label className="text-base font-medium">
                                                Risk Management
                                            </Label>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <Label className="text-sm text-muted-foreground mb-2 block font-medium">
                                                    Stop Loss
                                                </Label>
                                                <Input
                                                    type="number"
                                                    step="0.00001"
                                                    placeholder="0.00000"
                                                    {...register('stopLoss', {
                                                        min: {
                                                            value: 0,
                                                            message: 'Stop loss must be positive',
                                                        },
                                                    })}
                                                    className="h-10 text-sm"
                                                />
                                                {errors.stopLoss && (
                                                    <div className="flex items-center space-x-1 mt-1 text-sm text-red-500">
                                                        <AlertCircle className="h-4 w-4" />
                                                        <span>{errors.stopLoss.message}</span>
                                                    </div>
                                                )}
                                            </div>

                                            <div>
                                                <Label className="text-sm text-muted-foreground mb-2 block font-medium">
                                                    Take Profit
                                                </Label>
                                                <Input
                                                    type="number"
                                                    step="0.00001"
                                                    placeholder="0.00000"
                                                    {...register('takeProfit', {
                                                        min: {
                                                            value: 0,
                                                            message: 'Take profit must be positive',
                                                        },
                                                    })}
                                                    className="h-10 text-sm"
                                                />
                                                {errors.takeProfit && (
                                                    <div className="flex items-center space-x-1 mt-1 text-sm text-red-500">
                                                        <AlertCircle className="h-4 w-4" />
                                                        <span>{errors.takeProfit.message}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <Separator />

                                    {/* Partial Close Section */}
                                    <div className="space-y-4">
                                        <div className="flex items-center gap-2">
                                            <Minus className="h-4 w-4 text-muted-foreground" />
                                            <Label className="text-base font-medium">
                                                Partial Close
                                            </Label>
                                        </div>

                                        <div>
                                            <Label className="text-sm text-muted-foreground mb-2 block font-medium">
                                                Partial Close Amount
                                            </Label>
                                            <Input
                                                type="number"
                                                step="0.01"
                                                placeholder={`0.01 - ${formatAmount(Number(order.lot_size || 0), 5)}`}
                                                {...register('partialCloseAmount', {
                                                    min: {
                                                        value: 0.01,
                                                        message: 'Amount must be at least 0.01',
                                                    },
                                                    max: {
                                                        value: order.lot_size - 0.01,
                                                        message: `Amount must be less than ${formatAmount(Number(order.lot_size || 0), 5)}`,
                                                    },
                                                })}
                                                className="h-10 text-sm"
                                            />
                                            <p className="text-xs text-muted-foreground mt-1">
                                                Current lot size:{' '}
                                                {formatAmount(Number(order.lot_size || 0), 5)} lots
                                            </p>
                                            {errors.partialCloseAmount && (
                                                <div className="flex items-center space-x-1 mt-1 text-sm text-red-500">
                                                    <AlertCircle className="h-4 w-4" />
                                                    <span>{errors.partialCloseAmount.message}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </form>
                            </CardContent>
                        </Card>

                        {/* Action Buttons */}
                        <Card>
                            <CardHeader className="pb-3">
                                <CardTitle className="text-lg flex items-center gap-2">
                                    <Clock className="h-4 w-4" />
                                    Actions
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {/* Primary Actions */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <Button
                                        type="button"
                                        variant="secondary"
                                        onClick={handleSubmit(handlePartialClose)}
                                        disabled={
                                            updateOrderMutation.isPending ||
                                            cancelOrderMutation.isPending ||
                                            closeOrderMutation.isPending
                                        }
                                        className="gap-2 h-11"
                                    >
                                        <Minus className="h-4 w-4" />
                                        Partial Close Order
                                    </Button>

                                    <Button
                                        type="button"
                                        onClick={handleSubmit(onSubmit)}
                                        disabled={
                                            updateOrderMutation.isPending ||
                                            cancelOrderMutation.isPending ||
                                            closeOrderMutation.isPending
                                        }
                                        className="gap-2 h-11"
                                    >
                                        <Save className="h-4 w-4" />
                                        {updateOrderMutation.isPending
                                            ? 'Updating...'
                                            : 'Update Order'}
                                    </Button>
                                </div>

                                <Separator />

                                {/* Destructive Actions */}
                                <div className="space-y-3">
                                    <Label className="text-sm font-medium text-muted-foreground">
                                        Danger Zone
                                    </Label>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <Button
                                            type="button"
                                            variant="destructive"
                                            onClick={handleCancelOrder}
                                            disabled={
                                                updateOrderMutation.isPending ||
                                                cancelOrderMutation.isPending ||
                                                closeOrderMutation.isPending
                                            }
                                            className="gap-2 h-11"
                                        >
                                            <XCircle className="h-4 w-4" />
                                            Cancel Order
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="destructive"
                                            onClick={handleDeleteOrder}
                                            disabled={
                                                updateOrderMutation.isPending ||
                                                cancelOrderMutation.isPending ||
                                                closeOrderMutation.isPending
                                            }
                                            className="gap-2 h-11"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                            Delete Order
                                        </Button>
                                    </div>
                                </div>

                                {/* Dialog Actions */}
                                <div className="flex gap-3 pt-2">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={onClose}
                                        disabled={
                                            updateOrderMutation.isPending ||
                                            cancelOrderMutation.isPending ||
                                            closeOrderMutation.isPending
                                        }
                                        className="flex-1 h-11"
                                    >
                                        Close
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Partial Close Confirmation Dialog */}
            <AlertDialog open={partialCloseDialogOpen} onOpenChange={setPartialCloseDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Confirm Partial Close</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to partially close{' '}
                            {getValues().partialCloseAmount} lots of this order? This action cannot
                            be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmPartialClose}
                            disabled={closeOrderMutation.isPending}
                        >
                            {closeOrderMutation.isPending ? 'Closing...' : 'Confirm Close'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Cancel Order Confirmation Dialog */}
            <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Confirm Order Cancellation</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to cancel this order? This action cannot be
                            undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmCancelOrder}
                            disabled={cancelOrderMutation.isPending}
                        >
                            {cancelOrderMutation.isPending ? 'Cancelling...' : 'Confirm Cancel'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Delete Order Confirmation Dialog */}
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Confirm Order Deletion</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to delete this order? This action cannot be undone
                            and will permanently remove the order from the system.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmDeleteOrder}
                            disabled={cancelOrderMutation.isPending}
                            className="bg-red-600 hover:bg-red-700"
                        >
                            {cancelOrderMutation.isPending ? 'Deleting...' : 'Confirm Delete'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};
